import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import { useNavigate } from 'react-router-dom';

const STATUS_CONFIG = {
  scheduled: { label: 'Scheduled', color: '#3B82F6', icon: 'Calendar' },
  in_progress: { label: 'In Progress', color: '#F59E0B', icon: 'Zap' },
  completed: { label: 'Completed', color: '#22C55E', icon: 'CheckCircle' },
  cancelled: { label: 'Cancelled', color: '#EF4444', icon: 'XCircle' },
};

const JobsStatusWidget = ({ orgId }) => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!orgId) return;
    const fetch = async () => {
      setLoading(true);
      try {
        const { data } = await supabase?.from('jobs')?.select('status')?.eq('org_id', orgId)?.is('deleted_at', null);

        const counts = { scheduled: 0, in_progress: 0, completed: 0, cancelled: 0 };
        (data || [])?.forEach(j => { if (counts?.[j?.status] !== undefined) counts[j.status]++; });

        setStats(
          Object.entries(counts)?.map(([status, count]) => ({
            status,
            count,
            ...STATUS_CONFIG?.[status],
          }))
        );
      } catch (err) {
        console.error('Jobs status error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [orgId]);

  const total = stats?.reduce((s, d) => s + d?.count, 0);
  const pieData = stats?.filter(s => s?.count > 0);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload?.[0]?.payload;
    return (
      <div className="bg-popover border border-border rounded-lg px-3 py-2 shadow-lg text-sm">
        <span className="font-semibold text-foreground">{d?.label}: </span>
        <span className="text-muted-foreground">{d?.count}</span>
      </div>
    );
  };

  return (
    <motion.div
      className="bg-card border border-border rounded-2xl p-5 shadow-sm"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.15 }}
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-foreground">Jobs Overview</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{total} total jobs</p>
        </div>
        <button onClick={() => navigate('/jobs')} className="text-xs text-primary hover:underline font-medium">View all →</button>
      </div>
      {loading ? (
        <div className="space-y-3">
          {[1,2,3,4]?.map(i => <div key={i} className="h-10 bg-muted animate-pulse rounded-lg" />)}
        </div>
      ) : total === 0 ? (
        <div className="text-center py-6">
          <Icon name="Briefcase" size={28} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No jobs yet</p>
          <button onClick={() => navigate('/jobs')} className="text-xs text-primary hover:underline mt-1">Create first job</button>
        </div>
      ) : (
        <div className="flex items-center gap-4">
          <div className="w-28 h-28 flex-shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={28} outerRadius={50} dataKey="count" strokeWidth={0}>
                  {pieData?.map((entry, i) => <Cell key={i} fill={entry?.color} />)}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex-1 space-y-2">
            {stats?.map(s => (
              <div key={s?.status} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: s?.color }} />
                  <span className="text-xs text-muted-foreground">{s?.label}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-foreground">{s?.count}</span>
                  {total > 0 && (
                    <span className="text-xs text-muted-foreground w-8 text-right">{Math.round((s?.count / total) * 100)}%</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default JobsStatusWidget;
